import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, X, AlertTriangle, CheckCircle2, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { fmtNumber } from "@/lib/format";

function Dropzone({ label, file, onFile, testId, inputTestId, accent = "blue" }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const accentClass = accent === "blue" ? "text-[#0A3D91]" : "text-[#18B7D8]";
  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => ref.current?.click()}
      className={`drop-zone p-6 cursor-pointer ${drag ? "active" : ""}`}
    >
      <input
        ref={ref}
        type="file"
        accept=".xlsx"
        hidden
        data-testid={inputTestId}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
      {!file ? (
        <div className="text-center py-4">
          <UploadCloud className={`h-9 w-9 mx-auto ${accentClass}`} />
          <div className="font-display font-semibold text-sm text-slate-900 mt-3">{label}</div>
          <div className="text-xs text-slate-500 mt-1">
            Drop your .xlsx file here or click to browse
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
            <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · {label}</div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFile(null); }}
            className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function UploadCurve() {
  const nav = useNavigate();
  const [curveName, setCurveName] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [universeSize, setUniverseSize] = useState("");
  const [notes, setNotes] = useState("");
  const [tvFile, setTvFile] = useState(null);
  const [olvFile, setOlvFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const validate = () => {
    if (!curveName.trim()) return "Curve Name is required";
    if (!tvFile) return "Please upload the TV / GRP XLSX file";
    if (!olvFile) return "Please upload the OLV / YouTube XLSX file";
    if (!tvFile.name.toLowerCase().endsWith(".xlsx")) return "TV file must be .xlsx";
    if (!olvFile.name.toLowerCase().endsWith(".xlsx")) return "OLV file must be .xlsx";
    return null;
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("curve_name", curveName);
      if (targetAudience) fd.append("target_audience", targetAudience);
      if (universeSize) fd.append("universe_size", universeSize);
      if (notes) fd.append("notes", notes);
      fd.append("tv_file", tvFile);
      fd.append("olv_file", olvFile);
      const { data } = await api.post("/curve-sets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Curve set "${data.curve_name}" saved successfully.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    setCurveName(""); setTargetAudience(""); setUniverseSize(""); setNotes("");
    setTvFile(null); setOlvFile(null); setResult(null);
  };

  return (
    <div className="max-w-6xl space-y-6">
      <div className="card-harmony overflow-hidden">
        <div className="harmony-accent-bar h-1.5" />
        <div className="p-6">
          <h2 className="font-display text-2xl font-bold text-slate-900">Upload Curve Files</h2>
          <p className="text-sm text-slate-500 mt-1">
            Upload one TV/GRP and one OLV/YouTube XLSX file. Both files are saved together as a single Curve Set.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: form fields */}
        <div className="lg:col-span-1 card-harmony p-5 space-y-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Curve Name <span className="text-rose-500">*</span>
            </label>
            <input
              value={curveName}
              onChange={(e) => setCurveName(e.target.value)}
              data-testid={TID.uploadCurveName}
              placeholder="Type curve name"
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Target Audience
            </label>
            <input
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              data-testid={TID.uploadTargetAudience}
              placeholder="Type target audience"
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Universe Size
            </label>
            <input
              type="number"
              value={universeSize}
              onChange={(e) => setUniverseSize(e.target.value)}
              data-testid={TID.uploadUniverseSize}
              placeholder="85114562"
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={TID.uploadNotes}
              rows={3}
              placeholder="Optional notes about this curve set..."
              className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={busy}
              data-testid={TID.uploadSubmit}
              className="btn-primary flex-1 justify-center"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {busy ? "Processing…" : "Upload & Process"}
            </button>
            <button
              type="button"
              onClick={onReset}
              data-testid={TID.uploadReset}
              className="btn-secondary"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Right: dropzones + result */}
        <div className="lg:col-span-2 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="card-harmony p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-md bg-[#0A3D91]/10 text-[#0A3D91] text-xs font-bold flex items-center justify-center">TV</div>
                <div className="font-display font-semibold text-slate-900">GRP / TV Curve File</div>
              </div>
              <Dropzone
                label="TV / GRP Reach Curve (.xlsx)"
                file={tvFile}
                onFile={setTvFile}
                testId={TID.uploadTvDropzone}
                inputTestId={TID.uploadTvInput}
                accent="blue"
              />
              <div className="text-[11px] text-slate-500 mt-2">Expected columns: GRP, 1+, 2+, … 11+</div>
            </div>

            <div className="card-harmony p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-md bg-[#18B7D8]/15 text-[#0E73C9] text-xs font-bold flex items-center justify-center">OLV</div>
                <div className="font-display font-semibold text-slate-900">OLV / YouTube Curve File</div>
              </div>
              <Dropzone
                label="OLV / YouTube Reach Curve (.xlsx)"
                file={olvFile}
                onFile={setOlvFile}
                testId={TID.uploadOlvDropzone}
                inputTestId={TID.uploadOlvInput}
                accent="cyan"
              />
              <div className="text-[11px] text-slate-500 mt-2">Expected columns: Impressions (or GRP), 1+ (… 11+ optional)</div>
            </div>
          </div>

          {result && (
            <div className="card-harmony p-5" data-testid="upload-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <div className="font-display font-semibold text-slate-900">
                  Curve Set "{result.curve_name}" uploaded successfully
                </div>
              </div>

              {result.warnings?.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
                  {result.warnings.map((w) => (
                    <div key={w} className="flex items-start gap-2 text-xs text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {["tv", "olv"].map((k) => {
                  const sec = result[k];
                  return (
                    <div key={k} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-slate-500">{k.toUpperCase()} File</div>
                          <div className="text-sm font-semibold text-slate-900">{sec.file_name}</div>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <div>Input: <b>{sec.input_metric}</b></div>
                          <div>Rows: <b>{sec.row_count}</b></div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left py-2 px-3 font-medium text-slate-500 uppercase tracking-wider">{sec.input_metric}</th>
                              {sec.reach_levels.slice(0, 5).map((lvl) => (
                                <th key={lvl} className="text-left py-2 px-3 font-medium text-slate-500 uppercase tracking-wider">{lvl}+</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sec.preview.map((r) => (
                              <tr key={r.input_value} className="border-t border-slate-100">
                                <td className="py-2 px-3 text-slate-900 font-medium">{fmtNumber(r.input_value)}</td>
                                {sec.reach_levels.slice(0, 5).map((lvl) => (
                                  <td key={lvl} className="py-2 px-3 text-slate-600">
                                    {r[`reach_${lvl}_plus`] != null ? `${Number(r[`reach_${lvl}_plus`]).toFixed(2)}%` : "—"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex gap-2">
                <button onClick={() => nav("/curve-library")} className="btn-primary">
                  <Eye className="h-4 w-4" /> Open in Curve Library
                </button>
                <button onClick={() => nav("/optimizer")} className="btn-secondary">
                  Use in Optimizer →
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
